import type {
  AdvisedProduct,
  Integration,
  UnionOfType,
} from "../../../../app/types"
import type { Product } from "../../types"
import type {
  AdvisorInfo,
  SessionContext,
  UsageAnalyticsEventTransformer,
} from "../types"

type BaseEvent<T> = {
  type: T
  integration: "aiden:bloomreach"
  advisorId: string
  advisorName: string
}

type BloomReachAnsweredEvent = UnionOfType<
  BaseEvent<"aiden-answered"> & {
    eventData: {
      advisorId: string
      advisorName: string
      question: string
      answers: string[]
    }
  }
>

type BloomReachAdvisedEvent = UnionOfType<
  BaseEvent<"aiden-advised"> & {
    eventData: {
      advisorId: string
      advisorName: string
      advice: { id: string; name: string; image: string; link: string }[]
    }
  }
>

type BloomReachProductAdvisedEvent = UnionOfType<
  BaseEvent<"aiden-product-advised"> & {
    eventData: {
      advisorId: string
      advisorName: string
      id: string
      name: string
      image: string
      link: string
    }
  }
>

type BloomReachAdvicePageCustomerInfoEvent = UnionOfType<
  BaseEvent<"aiden-advice-page-customer-info-form"> & {
    eventData: {
      advisorId: string
      advisorName: string
      email: string
      checkboxChecked: boolean
      checkboxContext: string
      advice: { id: string; name: string; image: string; link: string }[]
    }
  }
>

type BloomReachNoResultsPageCustomerInfoEvent = UnionOfType<
  BaseEvent<"aiden-no-results-page-customer-info-form"> & {
    eventData: {
      advisorId: string
      advisorName: string
      email: string
      checkboxChecked: boolean
      checkboxContext: string
    }
  }
>
type BloomReachInfoPageCustomerInfoEvent = UnionOfType<
  BaseEvent<"aiden-info-page-customer-info-form"> & {
    eventData: {
      advisorId: string
      advisorName: string
      email: string
      checkboxChecked: boolean
      checkboxContext: string
      formIdentifier: string
    }
  }
>

type BloomReachProductClickedEvent = UnionOfType<
  BaseEvent<"aiden-product-clicked"> & {
    eventData: {
      advisorId: string
      advisorName: string
      product: {
        id: string
        name: string
        link: string
      }
    }
  }
>

type BloomReachProductAddToCartEvent = UnionOfType<
  BaseEvent<"aiden-product-add-to-cart"> & {
    eventData: {
      advisorId: string
      advisorName: string
      product: {
        id: string
        name: string
        link: string
        quantity: number
      }
    }
  }
>

/**
 *
 * The standard usage events are the events defined by Aiden itself.
 * These are used by clients for custom integration scripts and therefore,
 * the events can only be changed if they don't break backwards compatibility.
 *
 * */
export const BloomReachUsageEvents: UsageAnalyticsEventTransformer = {
  questionAnswered: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    question: { title: string; label: string },
    answers: string[]
  ): BloomReachAnsweredEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:bloomreach",
        type: "aiden-answered",
        eventData: {
          advisorId: advisor.id,
          advisorName: advisor.name,
          question: question.title,
          answers: answers,
        },
      },
    ]
  },

  adviceGiven: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    products: Product[]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  ): (BloomReachAdvisedEvent | BloomReachProductAdvisedEvent)[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:bloomreach",
        type: "aiden-advised",
        eventData: {
          advisorId: advisor.id,
          advisorName: advisor.name,
          advice: products.map((p) => ({
            id: p.clientProductId,
            name: p.name,
            image: p.image,
            link: p.link,
          })),
        },
      },
      ...products.map((product) => {
        const event: BloomReachProductAdvisedEvent = {
          advisorId: advisor.id,
          advisorName: advisor.name,
          integration: "aiden:bloomreach",
          type: "aiden-product-advised",
          eventData: {
            advisorId: advisor.id,
            advisorName: advisor.name,
            id: product.clientProductId,
            name: product.name,
            image: product.image,
            link: product.link,
          },
        }
        return event
      }),
    ]
  },

  clickedOnProduct: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    product: Product
  ): BloomReachProductClickedEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:bloomreach",
        type: "aiden-product-clicked",
        eventData: {
          advisorId: advisor.id,
          advisorName: advisor.name,
          product: {
            id: product.clientProductId,
            name: product.name,
            link: product.link,
          },
        },
      },
    ]
  },

  customerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
      advisedProducts: AdvisedProduct[]
    }
  ): BloomReachAdvicePageCustomerInfoEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:bloomreach",
        type: "aiden-advice-page-customer-info-form",
        eventData: {
          advisorId: advisor.id,
          advisorName: advisor.name,
          email: normalizeEmail(info.email),
          checkboxChecked: info.checkboxChecked,
          checkboxContext: info.checkboxText,
          advice: info.advisedProducts.map((p) => ({
            id: p.id,
            name: p.name,
            image: p.imageUrl,
            link: p.url,
          })),
        },
      },
    ]
  },

  infoPageCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
      formIdentifier: string
    }
  ): BloomReachInfoPageCustomerInfoEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:bloomreach",
        type: "aiden-info-page-customer-info-form",
        eventData: {
          advisorId: advisor.id,
          advisorName: advisor.name,
          email: normalizeEmail(info.email),
          checkboxChecked: info.checkboxChecked,
          checkboxContext: info.checkboxText,
          formIdentifier: info.formIdentifier,
        },
      },
    ]
  },

  noResultsCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
    }
  ): BloomReachNoResultsPageCustomerInfoEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:bloomreach",
        type: "aiden-no-results-page-customer-info-form",
        eventData: {
          advisorId: advisor.id,
          advisorName: advisor.name,
          email: normalizeEmail(info.email),
          checkboxChecked: info.checkboxChecked,
          checkboxContext: info.checkboxText,
        },
      },
    ]
  },

  //
    // These events don't have a usecase (yet)
    //
  conversationStarted: () => [],
}

function normalizeEmail(email: string): string {
  return email.toLowerCase().trim()
}
